import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  isGroup,
  TGroupConfig,
  TLayoutConfig,
  TLayoutEntry,
} from 'react-lib/components/organisms/CustomFieldGroupsSettings';
import ImportCustomFieldGroups, {
  ImportCustomFieldGroupsProps,
  ImportCustomFieldGroupsSelection,
  TSelectedLayoutEntry,
  TTargetCustomField,
} from 'react-lib/components/organisms/ImportCustomFieldGroups';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
  deconstructConditionalFormula,
  deconstructFormula,
} from 'src/app/project/components/formula-builder/utils/deconstruct-formula';
import {
  EFormulaPieceType,
  TFormulaPiece,
} from 'src/app/project/components/formula-builder/utils/formula-input.type';
import { CustomFieldsLayoutApiProviderService } from 'src/app/project/data-providers/api-providers/custom-fields-provider/custom-fields-provider';
import { ReactWrapperDirective } from 'src/app/project/directives/react-wrapper-directive';
import { TAccount } from 'src/app/project/modules/account/account.model';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { logErrorInSentry } from '../../../errors/response-error';
import { TCustomField, TWorkspaceCustomFields } from '../../custom-fields.model';
import { CustomFieldsService } from '../../custom-fields.service';
import { flattenFieldsLayout } from '../../utils/flatten-fields-layout';

const CONDITIONAL_FORMULA_PREFIX = 'if';

const getFormulaOperandIds = (formula: string): string[] => {
  const pieces: TFormulaPiece[] = formula.startsWith(CONDITIONAL_FORMULA_PREFIX)
    ? Object.values(deconstructConditionalFormula(formula)).flat()
    : deconstructFormula(formula);

  return pieces
    .filter((piece) => piece.type === EFormulaPieceType.CUSTOM_FIELD)
    .map((piece) => String(piece.value));
};

const isGroupConfig = (layoutItem: TLayoutConfig): layoutItem is TGroupConfig =>
  layoutItem.type === 'GROUP';

const getGroupTitles = (layoutConfig: TLayoutConfig[]): string[] =>
  layoutConfig.filter(isGroupConfig).map((group) => group.title);

const getGroupTitlesById = (layout: TLayoutEntry[]): Record<string, string> =>
  Object.fromEntries(
    layout
      .filter(isGroup)
      .filter((group) => group.id)
      .map((group) => [group.id, group.title]),
  );

const getRepeatedGroupTitles = (
  fieldsLayout: TSelectedLayoutEntry[],
  groupTitlesById: Record<string, string>,
  existingGroupTitles: string[],
): string[] =>
  fieldsLayout
    .filter((entry) => entry.type === 'GROUP')
    .map((entry) => groupTitlesById[entry.id])
    .filter((title) => existingGroupTitles.includes(title));

const isOperandCovered = (
  operandId: string,
  selectedFieldIds: string[],
  siteFields: TCustomField[],
  targetCustomFields: TTargetCustomField[],
): boolean => {
  if (selectedFieldIds.includes(operandId)) {
    return true;
  }

  const operand = siteFields.find((field) => field.id === operandId);

  if (!operand) {
    return false;
  }

  return targetCustomFields.some(
    (targetField) => targetField.label === operand.label && targetField.type === operand.type,
  );
};

const getFormulaFieldsMissingOperands = (
  siteFields: TCustomField[],
  selectedFieldIds: string[],
  targetCustomFields: TTargetCustomField[],
): string[] =>
  siteFields
    .filter((field) => field.formula && selectedFieldIds.includes(field.id))
    .filter((field) =>
      getFormulaOperandIds(field.formula).some(
        (operandId) =>
          !isOperandCovered(operandId, selectedFieldIds, siteFields, targetCustomFields),
      ),
    )
    .map((field) => field.label);

@Component({
  selector: 'pp-add-custom-field-groups-import',
  templateUrl: './add-custom-field-groups-import.component.html',
  styleUrl: './add-custom-field-groups-import.component.scss',
  standalone: true,
  imports: [ReactWrapperDirective],
})
export class AddCustomFieldGroupsImportComponent implements OnInit, OnDestroy {
  @Input() ppWorkspaceId: string;
  @Input() ppTargetGroupId: string;

  @Output() ppSelectionChange = new EventEmitter<ImportCustomFieldGroupsSelection>();
  @Output() ppFormulaFieldsMissingOperands = new EventEmitter<string[]>();
  @Output() ppExistingGroupNames = new EventEmitter<string[]>();

  ImportCustomFieldGroups = ImportCustomFieldGroups;

  reactProps: ImportCustomFieldGroupsProps = {
    accountOptions: [],
    siteOptions: [],
    layout: [],
    existingCustomFields: [],
    currentWorkspaceId: '',
    onSiteSelected: (workspaceId: string) => this.loadSiteLayout(workspaceId),
    onSelectionChange: (selection: ImportCustomFieldGroupsSelection) =>
      this.emitSelection(selection),
  };

  private siteFieldsByWorkspaceId: Record<string, TCustomField[]> = {};
  private groupTitlesByWorkspaceId: Record<string, Record<string, string>> = {};
  private existingGroupTitles: string[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly accounts$: Observable<TAccount[]>;
  private readonly workspaces$: Observable<TWorkspacesById>;

  constructor(
    private store: Store<{
      accounts: TAccount[];
      workspaces: TWorkspacesById;
    }>,
    private customFieldsService: CustomFieldsService,
    private customFieldsLayoutApiProviderService: CustomFieldsLayoutApiProviderService,
  ) {
    this.accounts$ = this.store.pipe(select(EStore.ACCOUNTS));
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
  }

  ngOnInit(): void {
    this.reactProps = {
      ...this.reactProps,
      existingCustomFields: this.getExistingCustomFields(),
      currentWorkspaceId: this.ppWorkspaceId,
      targetGroupId: this.ppTargetGroupId,
    };

    this.loadExistingGroupTitles();

    this.accounts$.pipe(takeUntil(this.destroy$)).subscribe((accounts) => {
      this.reactProps = {
        ...this.reactProps,
        accountOptions: accounts.map((account) => ({
          label: account.accountName,
          value: account.accountId,
        })),
      };
    });

    this.workspaces$.pipe(takeUntil(this.destroy$)).subscribe((workspaces) => {
      this.reactProps = {
        ...this.reactProps,
        siteOptions: Object.values(workspaces).map((workspace) => ({
          label: workspace.siteName,
          value: workspace.workspaceId,
          accountId: workspace.accountId,
          accountName: workspace.accountName,
          customFields: this.resolveSiteCustomFields(workspace),
        })),
      };
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  getSiteCustomFields = (
    customFieldIds: string[],
    siteCustomFields: TWorkspaceCustomFields,
  ): TCustomField[] =>
    customFieldIds
      .filter((customFieldId) => siteCustomFields[customFieldId])
      .map((customFieldId) => siteCustomFields[customFieldId]);

  private emitSelection(selection: ImportCustomFieldGroupsSelection): void {
    const targetCustomFields = this.getExistingCustomFields();

    this.ppSelectionChange.emit(selection);
    this.ppFormulaFieldsMissingOperands.emit(
      selection.sources.flatMap((source) =>
        getFormulaFieldsMissingOperands(
          this.siteFieldsByWorkspaceId[source.workspaceId] ?? [],
          flattenFieldsLayout(source.fieldsLayout),
          targetCustomFields,
        ),
      ),
    );
    this.ppExistingGroupNames.emit(
      selection.sources.flatMap((source) =>
        getRepeatedGroupTitles(
          source.fieldsLayout,
          this.groupTitlesByWorkspaceId[source.workspaceId] ?? {},
          this.existingGroupTitles,
        ),
      ),
    );
  }

  private loadExistingGroupTitles(): void {
    this.customFieldsLayoutApiProviderService
      .getAllCustomFieldsLayout(this.ppWorkspaceId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          logErrorInSentry({ error, message: 'Error fetching custom field layout' });

          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.existingGroupTitles = getGroupTitles(response.fieldsLayout);
      });
  }

  private loadSiteLayout(workspaceId: string): void {
    this.setLayout([]);

    this.customFieldsLayoutApiProviderService
      .getAllCustomFieldsLayout(workspaceId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          logErrorInSentry({ error, message: 'Error fetching custom field layout' });

          return EMPTY;
        }),
      )
      .subscribe((response) => {
        const siteCustomFields =
          this.customFieldsService.getWorkspaceCustomFields(workspaceId) ?? {};

        const layout = this.getSiteLayout(response.fieldsLayout, siteCustomFields);

        this.siteFieldsByWorkspaceId = {
          ...this.siteFieldsByWorkspaceId,
          [workspaceId]: layout.flatMap((entry) => (isGroup(entry) ? entry.items : [entry])),
        };

        this.groupTitlesByWorkspaceId = {
          ...this.groupTitlesByWorkspaceId,
          [workspaceId]: getGroupTitlesById(layout),
        };

        this.setLayout(layout);
      });
  }

  private setLayout(layout: TLayoutEntry[]): void {
    this.reactProps = { ...this.reactProps, layout };
  }

  private getSiteLayout(
    layoutConfig: TLayoutConfig[],
    siteCustomFields: TWorkspaceCustomFields,
  ): TLayoutEntry[] {
    return layoutConfig
      .map((layoutItem) => this.toLayoutEntry(layoutItem, siteCustomFields))
      .filter((entry): entry is TLayoutEntry => entry !== undefined);
  }

  private toLayoutEntry(
    layoutItem: TLayoutConfig,
    siteCustomFields: TWorkspaceCustomFields,
  ): TLayoutEntry | undefined {
    if (layoutItem.type === 'FIELD') {
      return siteCustomFields[layoutItem.id];
    }

    return {
      id: layoutItem.id,
      type: 'GROUP',
      title: layoutItem.title,
      description: layoutItem.description,
      items: this.getSiteCustomFields(
        layoutItem.items.map((groupItem) => groupItem.id),
        siteCustomFields,
      ),
    };
  }

  private resolveSiteCustomFields(workspace: TWorkspace): TCustomField[] {
    const siteCustomFields =
      this.customFieldsService.getWorkspaceCustomFields(workspace.workspaceId) ?? {};

    return this.getSiteCustomFields(workspace.customFields ?? [], siteCustomFields);
  }

  private getExistingCustomFields(): TTargetCustomField[] {
    const currentSiteCustomFields =
      this.customFieldsService.getWorkspaceCustomFields(this.ppWorkspaceId) ?? {};

    return Object.values(currentSiteCustomFields).map((customField) => ({
      label: customField.label,
      type: customField.type,
    }));
  }
}
